/**
 * Main template.
 */
import Context from '@shared/components/utils/context/mainContext';
import Description from '@components/sections/description/description';
import Focus from '@shared/types/enums/focus';
import Props from '@shared/types/props/main';
import Rails from '../sections/main/rails';
import Sliders from '../sections/main/sliders';
import TEmpty from './empty';
import useBackKeyboard from '@shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import useMain from '../../../shared/components/utils/hooks/useMain';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setFocus } from '@shared/store/slices/focus.slice';
import { store } from '@shared/store/store';
import { useAppDispatch } from '@shared/store/hooks';

export default function TMain(props: Props) {
  const dispatch = useAppDispatch();

  // Main hook

  const data = useMain(props);
  const {
    completeRef,
    currentRail,
    display,
    hasFilters,
    lastRail,
    prevFocus,
    railsRef,
    slidersRef,
    verticalAnimation,
  } = data;

  // Keyboard hooks

  useBackKeyboard({
    callback: () => {
      const { focus: focusSlice, router } = store.getState();
      if (focusSlice.focus === Focus.MENU || router.route.stack.length) {
        dispatch(popRoute());
      } else {
        prevFocus.current = focusSlice.focus as Focus | Focus[];
        dispatch(setFocus(Focus.MENU));
      }
    },
  });

  useKeyboard({
    canBeFocused: false,
    keyDownHandler: (key) => {
      const { focus: focusSlice } = store.getState();
      if (!railsRef.current.length && !slidersRef.current.length) return;
      if (!completeRef.current) return;
      const whitelistFocus = [Focus.MENU, Focus.SEARCH, Focus.LOGIN];
      if (!whitelistFocus.includes(focusSlice.focus as number)) return;

      const isSearchOrLogin =
        focusSlice.focus === Focus.SEARCH || focusSlice.focus === Focus.LOGIN;
      const fallbackFocus =
        (focusSlice?.stack?.length ? focusSlice.stack[0] : null) ||
        (!slidersRef.current.length
          ? [Focus.RAILS_LIST, parseInt(Focus.RAILS_SUB_LIST + '0')]
          : Focus.SLIDERS);

      switch (key) {
        case 'RIGHT':
          if (isSearchOrLogin) {
            dispatch(setFocus(fallbackFocus));
          } else {
            dispatch(
              setFocus(prevFocus.current ? prevFocus.current : fallbackFocus)
            );
          }
          break;
        case 'SELECT':
          if (isSearchOrLogin) return;
          dispatch(
            setFocus(prevFocus.current ? prevFocus.current : fallbackFocus)
          );
          break;
      }
      prevFocus.current = undefined;
    },
  });

  // Components data

  const contextValue = {
    ...data,
    hasFilters,
    animation: { vertical: verticalAnimation },
    complete: completeRef.current,
    lastRail: lastRail.current,
    index: currentRail,
    prevFocus: prevFocus.current,
    rails: railsRef.current,
    sliders: slidersRef.current,
  };

  return display?.empty ? (
    <TEmpty />
  ) : (
    <Context.Provider value={contextValue}>
      <Sliders />
      <Description />
      <Rails />
    </Context.Provider>
  );
}
